
/* ==========================================================================
   APPLICATION ENTRY POINT & EVENT DELEGATOR (/database/main.js)
   ========================================================================== */

import { state, setSelectedComponent, markSelectedComponentCustomized, clearSelectedComponent, getCaseModeSelection, setCaseModeSelection, resolveCaseModeId, getCaseStateKey, getSelectedComponentIdentity, getExactSelectedComponentSpecs } from './state.js?v=20260819-audit2';
import { getCaseIndexBySlug, setFilteredResults, getFilteredResult } from './js/runtime-index.js?v=20260819-audit2';
import { formatCaseBrandTitle, debounce, parseSpecNum, slugifyUrlSegment } from './utils.js?v=20260819-audit2';
import { 
  updateExplorerUiTitle, bridgeCaseTo3DWorkbench, isSingleCaseUrlView, 
  togglePinCase, syncCaseUrlAndTitle, initPopStateRouter, parseIncomingComparisonRoute,
  parseIncomingUrlParams, processUrlHashActions
} from './seo.js?v=20260822-routeexit1';
import { 
  openReportModal, closeReportModal, invalidateActiveReport, 
  triggerReportGeneration, initReportModalListeners 
} from './reports.js?v=20260822-popular2';
import { 
  checkCompatibility, loadApplicationData, applyCaseCompatibleDefaults 
} from './compatibility-engine.js?v=20260819-audit2';
import { 
  renderTelemetryPanel, renderPinDock, updateSliderBadges, 
  updateThemeToggleUI, cleanupOrphanNoteModals, 
  renderVisibleCardFeed, renderMoreCardFeed,
  syncPcieSlotsFromThickness, dynamicSliderFill, redrawActiveBlueprints,
  initCardFeedDelegation, initMobileDrawers, initControlBarListeners, setupLazyLoadingEngine,
  initHardwareInsightsUi
} from './ui.js?v=20260822-popular2';
import { initSearchableCombobox, populateHardwareDropdowns, populateCaseSearchDropdown } from './js/combobox.js?v=20260822-audit3';
import { logComponentSelection, dispatchSliderTelemetryDebounced } from './telemetry.js?v=20260822-popular2';
import { predictHardwareClearance, predictRelationalClearances, getConfirmedBuildEvidence } from './ml-engine.js?v=20260819-phase35';
import { debugRuntimeIssue, stableKey } from './runtime.js?v=20260819-audit2';
import { runtimeNow, debugPerformanceMetric, scheduleIdleTask } from './js/runtime-perf.js?v=20260819-audit2';
import { closeSpecNoteModal } from './ui-core.js?v=20260819-audit2';
import { normalizeHardwareIdentity, resolvePsuFormFactorFromCatalog } from './js/hardware-index.js?v=20260819-audit2';

/* --------------------------------------------------------------------------
   GLOBAL DISMISS & CLOSE DELEGATOR FOR NOTES MODALS
   -------------------------------------------------------------------------- */
document.addEventListener('click', (e) => {
  const closeBtn = e.target.closest('.spec-modal-close');
  if (closeBtn) {
    e.preventDefault();
    e.stopPropagation();
    const targetId = closeBtn.getAttribute('data-target');
    const box = targetId ? document.getElementById(targetId) : closeBtn.closest('.spec-dropdown-box');
    if (box) closeSpecNoteModal(box);
    return;
  }

  if (!e.target.closest('.spec-dropdown-box') && !e.target.closest('.spec-micro-toggle')) {
    cleanupOrphanNoteModals();
  }
});

function initSffApp() {

  const dom = {
    search: document.getElementById('caseSearch'), 
    cooling: document.getElementById('coolingMode'),
    cpuH: document.getElementById('cpuHeight'), 
    radSize: document.getElementById('radiatorSize'),
    gpuL: document.getElementById('gpuLength'), 
    gpuT: document.getElementById('gpuThickness'),
    gpuH: document.getElementById('gpuHeight'), 
    gpuPcie: document.getElementById('gpuPcieSlots'),
    vol: document.getElementById('maxVolume'), 
    moboVal: document.getElementById('moboType'),
    psuVal: document.getElementById('psuType'), 
    list: document.getElementById('caseList'),
    stats: document.getElementById('fitStatsCounter'), 
    sliders: document.querySelectorAll('.sff-slider'),
    sortSelector: document.getElementById('caseSortOrder'), 
    themeToggle: document.getElementById('themeToggleBtn'), 
    radFanVal: document.getElementById('radFanType'),
    ramH: document.getElementById('ramHeight'),
    ramModules: document.getElementById('ramModules'),
    loadMore: document.getElementById('loadMoreBtn'),
    shareBtn: document.getElementById('shareBuildBtn'), 
    generateReportBtn: document.getElementById('generateReportBtn')
  };

  let suppressComponentTelemetry = false;

  // Deterministic compatibility is cached independently from search/layout/sort
  // presentation state. View-only changes can therefore reuse physical results.
  const compatibilityCache = new Map();
  const COMPATIBILITY_CACHE_LIMIT = 7000;
  let telemetryRenderFrame = 0;
  let pendingTelemetryRender = null;
  let lastImmediateTelemetryKey = '';
  let telemetryIsCleared = true;

  const maybeLogComponentSelection = (...args) => {
    if (!suppressComponentTelemetry) logComponentSelection(...args);
  };

  const debouncedEvaluate = debounce(() => evaluateCompatibility(false), 120);

  const usableBuildPart = (part) => {
    const name = String(part?.name || '').trim();
    return !!name && !['NONE', 'N/A', 'NA', 'CUSTOM', 'UNKNOWN'].includes(name.toUpperCase());
  };

  const getComboboxItemLabel = (item) => {
    if (!item) return '';
    const badge = item.querySelector?.('.combobox-ai-badge');
    let text = item.textContent || '';
    if (badge?.textContent) text = text.replace(badge.textContent, '');
    return text.trim();
  };

  const findBestComboboxItem = (containerId, part) => {
    if (!usableBuildPart(part)) return null;
    const container = document.getElementById(containerId);
    const modelResolved = container?._sffFindBestItem?.(part);
    if (modelResolved) return modelResolved;

    // Staggered-deploy fallback for an older combobox module.
    container?._sffEnsureOptions?.();
    const target = normalizeHardwareIdentity(part.name);
    if (!target) return null;
    const targetTokens = new Set(target.split(' ').filter(Boolean));
    let best = null;
    let bestScore = -Infinity;
    document.querySelectorAll(`#${containerId} .combobox-dropdown .combobox-item`).forEach(item => {
      const candidate = normalizeHardwareIdentity(getComboboxItemLabel(item));
      if (!candidate) return;
      let score = -Infinity;
      if (candidate === target) score = 10000;
      else if (candidate.includes(target) || target.includes(candidate)) score = 5000 - Math.abs(candidate.length - target.length);
      else {
        const candidateTokens = candidate.split(' ').filter(Boolean);
        const overlap = candidateTokens.filter(token => targetTokens.has(token)).length;
        if (overlap >= Math.min(3, targetTokens.size)) score = overlap * 100 - Math.abs(candidate.length - target.length);
      }
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    });
    return bestScore >= 250 ? best : null;
  };

  const setSegmentValue = (groupId, rawValue) => {
    let wanted = String(rawValue || '').trim().toUpperCase();
    if (!wanted) return false;
    if (groupId === 'moboSegmented') {
      if (wanted === 'M-ATX' || wanted === 'MICRO-ATX' || wanted === 'MICRO ATX') wanted = 'MATX';
      if (wanted === 'MINI-ITX' || wanted === 'MINI ITX') wanted = 'ITX';
    }
    if (groupId === 'psuSegmented') {
      wanted = wanted.replace('_', '-');
      if (wanted === 'SFXL') wanted = 'SFX-L';
      if (wanted === 'FLEX-ATX' || wanted === 'FLEXATX') wanted = 'FLEX';
      if (wanted === 'DC' || wanted === 'DCATX') wanted = 'DC-ATX';
    }
    const btn = [...document.querySelectorAll(`#${groupId} .segment-btn`)]
      .find(node => String(node.getAttribute('data-value') || '').trim().toUpperCase() === wanted);
    if (!btn) return false;
    btn.click();
    return true;
  };

  async function loadCommunityBuildIntoConfigurator(buildConfig) {
    if (!buildConfig || typeof buildConfig !== 'object' || !Array.isArray(state.fetchedCasesGlobal)) return false;
    // Loading a documented build is a new navigation transaction even when it targets the
    // same case. Do not leave an older immutable build token in the address bar.
    state.portableRouteExitRequested = true;
    const targetSlug = slugifyUrlSegment(buildConfig.case_slug || '');
    const wantedCaseId = String(buildConfig.case_catalog_id || buildConfig.caseCatalogId || '').trim().toLowerCase();
    let caseIdx = wantedCaseId
      ? state.fetchedCasesGlobal.findIndex(c => String(c?.catalogId || c?.catalog_id || c?.id || '').trim().toLowerCase() === wantedCaseId)
      : -1;
    if (caseIdx < 0) caseIdx = getCaseIndexBySlug(targetSlug);
    if (caseIdx < 0) return false;

    const caseObj = state.fetchedCasesGlobal[caseIdx];
    if (dom.search) dom.search.value = caseObj.name || '';
    state.userExplicitUnselect = false;
    state.routeNavigationIntent = 'replace';
    state.selectedCaseIdx = caseIdx;

    // Let normal case navigation clear stale component state first.
    evaluateCompatibility(true);

    const components = buildConfig.components || {};
    let requested = 0;
    let resolved = 0;
    suppressComponentTelemetry = true;
    try {
      if (usableBuildPart(components.mobo) || buildConfig.mobo_form_factor) {
        requested++;
        const moboItem = findBestComboboxItem('moboCombobox', components.mobo);
        const moboFormFactor = buildConfig.mobo_form_factor || moboItem?.getAttribute('data-form-factor') || '';
        const typeLoaded = moboFormFactor ? setSegmentValue('moboSegmented', moboFormFactor) : false;
        if (moboItem) moboItem.click();
        if (moboItem || typeLoaded) resolved++;
      }

      if (usableBuildPart(components.psu)) {
        requested++;
        const psuItem = findBestComboboxItem('psuCombobox', components.psu);
        if (psuItem) {
          psuItem.click();
          resolved++;
        } else {
          const psuType = resolvePsuFormFactorFromCatalog(components.psu, state.hardwareGlobal);
          if (psuType && setSegmentValue('psuSegmented', psuType)) resolved++;
        }
      }

      if (usableBuildPart(components.gpu)) {
        requested++;
        const gpuItem = findBestComboboxItem('gpuCombobox', components.gpu);
        if (gpuItem) {
          gpuItem.click();
          resolved++;
        }
      }

      const hasAio = usableBuildPart(components.aio);
      const hasAir = usableBuildPart(components.cooler);
      if (hasAio) {
        requested++;
        setSegmentValue('coolingModeSegmented', 'liquid');
        const aioItem = findBestComboboxItem('aioCoolerCombobox', components.aio) || findBestComboboxItem('aioSearchCombobox', components.aio);
        if (aioItem) {
          aioItem.click();
          resolved++;
        }
      } else if (hasAir) {
        requested++;
        setSegmentValue('coolingModeSegmented', 'air');
        const coolerItem = findBestComboboxItem('cpuCoolerCombobox', components.cooler);
        if (coolerItem) {
          coolerItem.click();
          resolved++;
        }
      }

      const requestedMode = String(buildConfig.case_mode || '').trim();
      if (requestedMode && requestedMode.toLowerCase() !== 'default') {
        requested++;
        const resolvedMode = resolveCaseModeId(caseObj, requestedMode, null);
        if (resolvedMode) {
          setCaseModeSelection(caseObj, resolvedMode);
          state.selectedModeId = resolvedMode;
          resolved++;
        }
      }

      // Community Build records can carry the exact adjustable-spine step separately
      // from the logical case mode. The compatibility engine validates/applies it on
      // the final evaluation instead of silently re-selecting a different step.
      state.portableFixedStep = String(buildConfig.case_fixed_step || buildConfig.fixedStep || '').trim() || null;
    } finally {
      suppressComponentTelemetry = false;
    }

    invalidateActiveReport();
    updateSliderBadges();
    evaluateCompatibility(true);
    return requested === 0 ? true : resolved === requested;
  }

  window.loadCommunityBuildIntoConfigurator = loadCommunityBuildIntoConfigurator;

  window.togglePinCase = (idx) => togglePinCase(idx, evaluateCompatibility);
  window.bridgeCaseTo3DWorkbench = bridgeCaseTo3DWorkbench;
  window.openReportModal = openReportModal;
  window.closeReportModal = closeReportModal;
  window.evaluateCompatibility = evaluateCompatibility;
  // ModeBridge4 diagnostic: exposes the exact DB receive/resolve state without
  // recomputing identity or changing the active configuration.
  window.getSffDbBridgeState = () => {
    const idx = state.selectedCaseIdx;
    const activeCase = idx !== null && idx >= 0 ? state.fetchedCasesGlobal?.[idx] || null : null;
    const result = idx !== null && idx >= 0 ? state.resultByIdx?.get?.(idx) || null : null;
    const resolved = result?.spineStep || null;
    const activeModeId = activeCase ? getCaseModeSelection(activeCase, 'default') : null;
    const activeMode = activeCase && activeModeId
      ? window.SFFContracts?.resolveModeRecord?.(activeCase.modes || [], activeModeId, { fallbackDefault: true })
      : null;
    return {
      build: '20260822-statebridge4',
      caseId: String(activeCase?.catalogId || activeCase?.catalog_id || activeCase?.id || ''),
      caseSlug: activeCase ? caseRouteSlug(activeCase) : '',
      modeId: activeModeId,
      modeLabel: String(activeMode?.mode_name || activeMode?.name || activeModeId || '') || null,
      availableModes: Array.isArray(activeCase?.modes)
        ? activeCase.modes.map(mode => String(mode?.mode_id || mode?.id || '')).filter(Boolean)
        : [],
      requestedFixedStep: state.portableFixedStep || null,
      resolvedFixedStep: String(resolved?.slotName || resolved?.id || resolved?.mode_id || resolved?.name || '') || null
    };
  };

  function readEvaluationForm() {
    const rawSearch = dom.search ? dom.search.value.toLowerCase().trim() : '';
    const exactPsu = getExactSelectedComponentSpecs('psu');
    const exactPsuLength = Number(exactPsu?.length_mm);
    return {
      search: rawSearch,
      tokens: rawSearch ? rawSearch.split(/\s+/).filter(Boolean) : [],
      cooling: dom.cooling ? dom.cooling.value : 'air',
      cpuH: dom.cpuH ? parseFloat(dom.cpuH.value) : 47,
      rad: dom.radSize ? parseInt(dom.radSize.value, 10) : 240,
      gpuL: dom.gpuL ? parseFloat(dom.gpuL.value) : 304,
      gpuT: dom.gpuT ? parseFloat(dom.gpuT.value) : 40,
      gpuH: dom.gpuH ? parseFloat(dom.gpuH.value) : 137,
      pcie: dom.gpuPcie ? parseFloat(dom.gpuPcie.value) : 2,
      maxVol: dom.vol ? parseFloat(dom.vol.value) : 10,
      mobo: dom.moboVal ? dom.moboVal.value.toUpperCase() : 'ITX',
      psu: dom.psuVal ? dom.psuVal.value.toUpperCase() : 'SFX',
      psuL: Number.isFinite(exactPsuLength) && exactPsuLength > 0 ? exactPsuLength : undefined,
      radFan: dom.radFanVal ? dom.radFanVal.value : 'standard',
      ramH: dom.ramH ? parseFloat(dom.ramH.value) : 35,
      ramModules: dom.ramModules ? parseInt(dom.ramModules.value, 10) : 2,
      fixedStep: state.portableFixedStep || null,
      sort: dom.sortSelector ? dom.sortSelector.value : 'vol-high'
    };
  }

  function compatibilitySignature(f) {
    const exactAio = getExactSelectedComponentSpecs('aio');
    return stableKey([
      'compat-v6-statebridge4', f.cooling, f.cpuH, f.rad, f.gpuL, f.gpuT, f.gpuH,
      f.pcie, f.mobo, f.psu, f.psuL ?? null, f.radFan, f.fixedStep || null,
      exactAio?.actual_thickness_mm ?? null
    ]);
  }

  function activeTelemetrySignature(f) {
    const gpu = getSelectedComponentIdentity('gpu');
    const cpu = getSelectedComponentIdentity('cpu');
    const aio = getSelectedComponentIdentity('aio');
    const psu = getSelectedComponentIdentity('psu');
    const ram = getSelectedComponentIdentity('ram');
    const cpuExact = getExactSelectedComponentSpecs('cpu');
    const aioExact = getExactSelectedComponentSpecs('aio');
    return stableKey([
      compatibilitySignature(f),
      gpu?.name || '', gpu?.customized ? 1 : 0,
      cpu?.name || '', cpu?.customized ? 1 : 0,
      aio?.name || '', aio?.customized ? 1 : 0,
      psu?.name || '', psu?.catalogId || '', psu?.customized ? 1 : 0,
      ram?.name || '', ram?.catalogId || '', ram?.customized ? 1 : 0,
      Number(f.ramH || 0), Number(f.ramModules || 0),
      cpuExact?.length_mm ?? null, cpuExact?.width_mm ?? null,
      aioExact?.actual_length_mm ?? null, aioExact?.actual_width_mm ?? null,
      aioExact?.actual_thickness_mm ?? null, aioExact?.pump_block_height_mm ?? null
    ]);
  }

  function rememberCompatibilityResult(cacheKey, value) {
    compatibilityCache.set(cacheKey, value);
    while (compatibilityCache.size > COMPATIBILITY_CACHE_LIMIT) {
      compatibilityCache.delete(compatibilityCache.keys().next().value);
    }
    return value;
  }

  function getCompatibilityResult(caseObj, idx, f, configSignature, stats = null) {
    const activeModeId = getCaseModeSelection(caseObj, 'default');
    const cacheKey = stableKey(['case-fit-v5-statebridge4', getCaseStateKey(caseObj), activeModeId, state.portableFixedStep || null, configSignature]);
    if (compatibilityCache.has(cacheKey)) {
      if (stats) stats.hits++;
      return compatibilityCache.get(cacheKey);
    }

    if (stats) stats.misses++;
    const res = checkCompatibility(caseObj, f, activeModeId);
    return rememberCompatibilityResult(cacheKey, {
      idx,
      fits: res.fits,
      warns: res.warns,
      fails: res.fails,
      unknowns: res.unknowns,
      advisories: res.advisories,
      spineStep: res.spineStep,
      activeSpecs: res.activeSpecs,
      _compatibilityCacheKey: cacheKey
    });
  }

  function scheduleTelemetryRender(activeCase, activeEval, f, isCurrentEvaluation) {
    pendingTelemetryRender = { activeCase, activeEval, f, isCurrentEvaluation };
    if (telemetryRenderFrame) return;
    const raf = typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (cb) => setTimeout(cb, 0);
    telemetryRenderFrame = raf(() => {
      telemetryRenderFrame = 0;
      const pending = pendingTelemetryRender;
      pendingTelemetryRender = null;
      if (!pending || !pending.isCurrentEvaluation()) return;
      renderTelemetryPanel(pending.activeCase, pending.activeEval, pending.f, pending.activeEval.aiResult || null);
    });
  }

  function resetTelemetryEvidenceIfNeeded(activeEval, signature) {
    if (activeEval._telemetrySignature === signature) return;
    activeEval._telemetrySignature = signature;
    delete activeEval.aiResult;
    delete activeEval.aiResidualRiskScore;
    delete activeEval.aiResidualRiskLevel;
    delete activeEval.buildEvidence;
    delete activeEval.gnnResult;
    delete activeEval.gnnDeltas;
    delete activeEval.gnnInteractions;
    delete activeEval._aiPromise;
    delete activeEval._buildEvidencePromise;
    delete activeEval._gnnPromise;
    delete activeEval._gnnScheduled;
  }

  function ensureActiveCaseTelemetry(activeCase, activeEval, f, isCurrentEvaluation) {
    if (activeEval.aiResult === undefined && !activeEval._aiPromise) {
      const promise = predictHardwareClearance(activeCase, f, activeEval.spineStep, activeEval)
        .then(aiResult => {
          if (!isCurrentEvaluation()) return aiResult;
          activeEval.aiResult = aiResult || null;
          activeEval.aiResidualRiskScore = aiResult?.residualRiskScore ?? null;
          activeEval.aiResidualRiskLevel = aiResult?.residualRiskLevel ?? null;
          scheduleTelemetryRender(activeCase, activeEval, f, isCurrentEvaluation);
          return aiResult;
        })
        .catch(err => debugRuntimeIssue('Classifier Async', err))
        .finally(() => { if (activeEval._aiPromise === promise) activeEval._aiPromise = null; });
      activeEval._aiPromise = promise;
    }

    if (activeEval.buildEvidence === undefined && !activeEval._buildEvidencePromise) {
      const promise = getConfirmedBuildEvidence(activeCase, f)
        .then(buildEvidence => {
          if (!isCurrentEvaluation()) return buildEvidence;
          activeEval.buildEvidence = buildEvidence || null;
          scheduleTelemetryRender(activeCase, activeEval, f, isCurrentEvaluation);
          return buildEvidence;
        })
        .catch(err => debugRuntimeIssue('Build Evidence Async', err))
        .finally(() => { if (activeEval._buildEvidencePromise === promise) activeEval._buildEvidencePromise = null; });
      activeEval._buildEvidencePromise = promise;
    }

    if (activeEval.gnnResult === undefined && !activeEval._gnnPromise && !activeEval._gnnScheduled) {
      activeEval._gnnScheduled = true;
      scheduleIdleTask(() => {
        activeEval._gnnScheduled = false;
        if (!isCurrentEvaluation()) return;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        if (activeEval.gnnResult !== undefined || activeEval._gnnPromise) return;

        const promise = predictRelationalClearances(activeCase, f, activeEval.spineStep, activeEval)
          .then(gnnResult => {
            if (!isCurrentEvaluation()) return gnnResult;
            activeEval.gnnResult = gnnResult || null;
            if (gnnResult) {
              activeEval.gnnDeltas = gnnResult.deltas;
              activeEval.gnnInteractions = gnnResult.interactions;
            }
            scheduleTelemetryRender(activeCase, activeEval, f, isCurrentEvaluation);
            return gnnResult;
          })
          .catch(err => debugRuntimeIssue('Relational GNN Async', err))
          .finally(() => { if (activeEval._gnnPromise === promise) activeEval._gnnPromise = null; });
        activeEval._gnnPromise = promise;
      }, 1800);
    }
  }

  function evaluateCompatibility(preservePagination = false) {
    const startedAt = runtimeNow();
    if (!state.fetchedCasesGlobal.length) return;
    if (!preservePagination) state.visibleLimit = window.innerWidth <= 1024 ? 24 : 50;

    syncCaseUrlAndTitle(dom.search?.value, applyCaseCompatibleDefaults);

    const f = readEvaluationForm();
    const configSignature = compatibilitySignature(f);
    const cacheStats = { hits: 0, misses: 0 };
    const visibleResults = [];
    state.totalFitsCount = 0;

    // View filtering/sorting is intentionally separate from deterministic physics.
    // Each candidate resolves through the config+case+mode compatibility cache.
    for (let i = 0; i < state.fetchedCasesGlobal.length; i++) {
      const c = state.fetchedCasesGlobal[i];
      if (f.tokens.length && !f.tokens.every(t => c._lcName.includes(t))) continue;
      if (!f.tokens.length && c._volume > f.maxVol) continue;

      if (!f.tokens.length) {
        if (state.activeLayout === 'apu') {
          if (c._layout !== 'apu') continue;
        } else {
          if (c._layout === 'apu') continue;
          if (state.activeLayout !== 'all' && c._layout !== state.activeLayout) continue;
        }
      }

      if (state.filterFlags.hideLegacy && c._isLegacy) continue;

      const result = getCompatibilityResult(c, i, f, configSignature, cacheStats);
      if (state.filterFlags.hideIncompatible && !result.fits) continue;
      if (result.fits) state.totalFitsCount++;

      let sortV = 0;
      if (f.sort === 'vol-low') sortV = c._volume > 0 ? (1000 - c._volume) : -500;
      else if (f.sort === 'vol-high') sortV = c._volume;
      else if (f.sort === 'gpu-high') sortV = c._maxGPU;
      else if (f.sort === 'price-low') sortV = c._priceVal > 0 ? (1000 - c._priceVal) : -500;
      result.score = (result.fits ? 10000 : 0) + sortV;
      visibleResults.push(result);
    }

    visibleResults.sort((a, b) => b.score - a.score);
    setFilteredResults(visibleResults);

    // Pinned/selected cases may sit outside the current view filters. Keep their
    // deterministic result indexed so comparison/telemetry never falls back to
    // an invented FIT merely because a presentation filter hid the case.
    state.sffPinnedCases.forEach(idx => {
      if (getFilteredResult(idx) || !state.fetchedCasesGlobal[idx]) return;
      state.resultByIdx.set(idx, getCompatibilityResult(state.fetchedCasesGlobal[idx], idx, f, configSignature, cacheStats));
    });

    if (!state.userExplicitUnselect && state.selectedCaseIdx === null) {
      if (isSingleCaseUrlView() && state.filteredResults.length > 0) {
        state.selectedCaseIdx = state.filteredResults[0].idx;
      } else if (state.filteredResults.length === 1 && state.filteredResults[0].fits) {
        state.selectedCaseIdx = state.filteredResults[0].idx;
      }
    }

    if (state.selectedCaseIdx !== null && state.fetchedCasesGlobal[state.selectedCaseIdx]) {
      const targetCaseIdx = state.selectedCaseIdx;
      const activeCase = state.fetchedCasesGlobal[targetCaseIdx];
      const activeModeId = getCaseModeSelection(activeCase, 'default');
      const activeEval = getFilteredResult(targetCaseIdx) || getCompatibilityResult(activeCase, targetCaseIdx, f, configSignature, cacheStats);
      state.resultByIdx.set(targetCaseIdx, activeEval);
      const telemetrySignature = activeTelemetrySignature(f);
      resetTelemetryEvidenceIfNeeded(activeEval, telemetrySignature);

      const isCurrentEvaluation = () => (
        state.selectedCaseIdx === targetCaseIdx &&
        state.fetchedCasesGlobal[targetCaseIdx] === activeCase &&
        getCaseModeSelection(activeCase, 'default') === activeModeId &&
        activeEval._telemetrySignature === telemetrySignature
      );

      if (dom.generateReportBtn) {
        const reportEligible = activeEval?.fits === true;
        const hasCurrentReport = !!(
          state.activeAuditReportData &&
          state.activeAuditTargetCaseIdx === targetCaseIdx
        );

        dom.generateReportBtn.disabled = !reportEligible;
        dom.generateReportBtn.classList.toggle('disabled-btn', !reportEligible);
        dom.generateReportBtn.setAttribute('aria-disabled', reportEligible ? 'false' : 'true');
        dom.generateReportBtn.dataset.reportEligible = reportEligible ? 'true' : 'false';

        const reportBtnText = document.getElementById('generateReportText');
        if (reportBtnText) {
          const caseTitle = formatCaseBrandTitle(activeCase.name).substring(0, 15);
          reportBtnText.innerText = !reportEligible
            ? `REPORT BLOCKED — NO FIT`
            : (hasCurrentReport
              ? `VIEW REPORT (${caseTitle})`
              : `GENERATE REPORT (${caseTitle})`);
        }
      }

      // Deterministic telemetry paints immediately only when the selected case,
      // mode, or physical configuration changes. View-only filtering/sorting keeps
      // the existing (possibly AI-enriched) telemetry DOM intact.
      const immediateTelemetryKey = stableKey([
        getCaseStateKey(activeCase), activeModeId, telemetrySignature,
        activeEval._compatibilityCacheKey || ''
      ]);
      if (lastImmediateTelemetryKey !== immediateTelemetryKey || telemetryIsCleared) {
        renderTelemetryPanel(activeCase, activeEval, f, activeEval.aiResult || null);
        lastImmediateTelemetryKey = immediateTelemetryKey;
        telemetryIsCleared = false;
      }
      ensureActiveCaseTelemetry(activeCase, activeEval, f, isCurrentEvaluation);
    } else {
      if (dom.generateReportBtn) {
        dom.generateReportBtn.disabled = true;
        const reportBtnText = document.getElementById('generateReportText');
        if (reportBtnText) reportBtnText.innerText = 'GENERATE REPORT';
      }
      if (!telemetryIsCleared) {
        renderTelemetryPanel(null, null, null);
        telemetryIsCleared = true;
        lastImmediateTelemetryKey = '';
      }
    }

    renderPinDock(state.sffPinnedCases, state.fetchedCasesGlobal, (unpinIdx) => {
      togglePinCase(unpinIdx, evaluateCompatibility);
    });
    renderVisibleCardFeed(dom);

    debugPerformanceMetric('evaluateCompatibility', startedAt, {
      visible: state.filteredResults.length,
      cacheHits: cacheStats.hits,
      cacheMisses: cacheStats.misses,
      cacheSize: compatibilityCache.size
    });
  }

  // Theme setup
  const curTheme = localStorage.getItem('sff-theme') || 'dark';
  if (curTheme === 'light') {
    document.documentElement.setAttribute('data-theme', 'light');
    updateThemeToggleUI(false);
  } else {
    document.documentElement.removeAttribute('data-theme');
    updateThemeToggleUI(true);
  }

  // Theme Toggle Event Listener Binding
  if (dom.themeToggle) {
    dom.themeToggle.addEventListener('click', () => {
      const isDark = !document.documentElement.hasAttribute('data-theme');
      try {
        if (isDark) {
          document.documentElement.setAttribute('data-theme', 'light');
          localStorage.setItem('sff-theme', 'light');
          updateThemeToggleUI(false);
        } else {
          document.documentElement.removeAttribute('data-theme');
          localStorage.setItem('sff-theme', 'dark');
          updateThemeToggleUI(true);
        }
      } catch(e) { debugRuntimeIssue('Theme Preference', e); }
      if (dom.sliders) dom.sliders.forEach(dynamicSliderFill);
      redrawActiveBlueprints(dom);
    });
  }

  // Generate Report Button Event Listener Binding
  if (dom.generateReportBtn) {
    dom.generateReportBtn.addEventListener('click', (e) => {
      e.preventDefault();
      triggerReportGeneration(dom, null);
    });
  }

  // Load More Cases Button Event Listener Binding
  if (dom.loadMore) {
    dom.loadMore.addEventListener('click', (e) => {
      e.preventDefault();
      state.visibleLimit += 50;
      renderMoreCardFeed(dom);
    });
  }

  // Sliders Listener
  if (dom.sliders) {
    dom.sliders.forEach(s => { 
      s.addEventListener('input', () => {
        invalidateActiveReport();
        if (s.id === 'gpuLength') markSelectedComponentCustomized('gpu', { length_mm: parseSpecNum(s.value, 304) });
        if (s.id === 'gpuThickness') markSelectedComponentCustomized('gpu', { thickness_mm: parseSpecNum(s.value, 40) });
        if (s.id === 'gpuHeight') markSelectedComponentCustomized('gpu', { height_mm: parseSpecNum(s.value, 137) });
        if (s.id === 'cpuHeight') markSelectedComponentCustomized('cpu', { height_mm: parseSpecNum(s.value, 47) });
        if (s.id === 'ramHeight') {
          markSelectedComponentCustomized('ram', { height_mm: parseSpecNum(s.value, 35) });
          state.portableBuildHardware.ram = null;
        }
        updateSliderBadges();
        debouncedEvaluate();
        dispatchSliderTelemetryDebounced();
      });
    });
  }

  // Sort Selector Listener
  if (dom.sortSelector) {
    dom.sortSelector.addEventListener('change', () => {
      debouncedEvaluate();
    });
  }

  // Segment buttons
  function attachSegmentGroup(groupId, hiddenInputId, callback) {
    const container = document.getElementById(groupId); if (!container) return;
    container.querySelectorAll('.segment-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (groupId !== 'layoutFilterGroup') invalidateActiveReport();
        if (btn.classList.contains('toggle-modifier')) return;
        container.querySelectorAll('.segment-btn').forEach(b => { 
          if (!b.classList.contains('toggle-modifier')) { 
            b.classList.remove('active'); 
            b.setAttribute('aria-pressed', 'false'); 
            b.setAttribute('aria-checked', 'false');
          } 
        });
        btn.classList.add('active'); 
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-checked', 'true');

        const val = btn.getAttribute('data-value') || btn.getAttribute('data-filter');

        const targetInput = document.getElementById(hiddenInputId);
        if (targetInput) targetInput.value = val;
        if (groupId === 'gpuPcieSegmented') markSelectedComponentCustomized('gpu', { pcie_slots: parseSpecNum(val, 2) });
        if (groupId === 'moboSegmented') clearSelectedComponent('mobo');
        if (groupId === 'psuSegmented' && !state.isRestoringShortcodeState) {
          clearSelectedComponent('psu');
          if (state.portableBuildHardware) state.portableBuildHardware.psu = null;
          const psuInput = document.querySelector('#psuCombobox .combobox-input');
          if (psuInput) psuInput.value = '';
          const psuClear = document.querySelector('#psuCombobox .combobox-clear-btn');
          if (psuClear) psuClear.style.display = 'none';
        }
        if (groupId === 'radiatorSizeSegmented') markSelectedComponentCustomized('aio', { radiator_size_mm: parseInt(val, 10) || 240 });
        if (callback) callback(val);

        if (groupId === 'coolingModeSegmented') { 
          const airP = document.getElementById('airCoolingPanel');
          const liqP = document.getElementById('liquidCoolingPanel');
          if (airP) airP.classList.toggle('active', val === 'air'); 
          if (liqP) liqP.classList.toggle('active', val === 'liquid'); 
          
          if (val === 'air') {
            clearSelectedComponent('aio');
          }
        }

        debouncedEvaluate();
      });
    });
  }

  attachSegmentGroup('layoutFilterGroup', null, (val) => { state.activeLayout = val || 'all'; });
  attachSegmentGroup('moboSegmented', 'moboType');
  attachSegmentGroup('psuSegmented', 'psuType');
  attachSegmentGroup('coolingModeSegmented', 'coolingMode');
  attachSegmentGroup('radiatorSizeSegmented', 'radiatorSize');
  attachSegmentGroup('radFanSegmented', 'radFanType');
  attachSegmentGroup('gpuPcieSegmented', 'gpuPcieSlots');
  attachSegmentGroup('ramModuleSegmented', 'ramModules', (val) => {
    const current = state.activeSelectedRamSpecs;
    if (current) {
      current.modules = Math.max(1, Math.min(8, parseInt(val, 10) || 2));
      if (current.original_specs) current.original_specs.modules = current.modules;
    }
    if (state.portableBuildHardware?.ram) state.portableBuildHardware.ram.modules = Math.max(1, Math.min(8, parseInt(val, 10) || 2));
    updateSliderBadges();
  });

  // Register Comboboxes
  initSearchableCombobox('moboCombobox', (item) => {
    invalidateActiveReport();
    const formFactor = item.getAttribute('data-form-factor') || 'ITX';
    const selectedLabel = item.getAttribute('data-label') || getComboboxItemLabel(item);
    const brand = String(item.getAttribute('data-brand') || '').trim();
    const model = String(item.getAttribute('data-model') || '').trim();
    const length = parseSpecNum(item.getAttribute('data-mobo-length'), 0);
    const width = parseSpecNum(item.getAttribute('data-mobo-width'), 0);
    setSelectedComponent('mobo', {
      name: selectedLabel, brand, model, form_factor: formFactor,
      length_mm: length > 0 ? length : null,
      width_mm: width > 0 ? width : null,
      dimension_known: { length_mm: length > 0, width_mm: width > 0 },
      dimension_source: item.getAttribute('data-dimension-source') || 'unknown',
      catalogId: item.getAttribute('data-catalog-id') || ''
    });
    updateSliderBadges(); debouncedEvaluate();
    maybeLogComponentSelection('mobo', { name: selectedLabel, form_factor: formFactor });
  }, null, debouncedEvaluate);

  initSearchableCombobox('psuCombobox', (item) => {
    invalidateActiveReport();
    const selectedLabel = item.getAttribute('data-label') || getComboboxItemLabel(item);
    const formFactor = item.getAttribute('data-form-factor') || (dom.psuVal ? dom.psuVal.value.toUpperCase() : 'SFX');
    const brand = String(item.getAttribute('data-brand') || '').trim();
    const model = String(item.getAttribute('data-model') || '').trim();
    const length = parseSpecNum(item.getAttribute('data-psu-length'), 0);
    const width = parseSpecNum(item.getAttribute('data-psu-width'), 0);
    const height = parseSpecNum(item.getAttribute('data-psu-height'), 0);
    const wattage = parseSpecNum(item.getAttribute('data-psu-wattage'), 0);

    // Exact PSU selection drives the existing form-factor authority instead of
    // creating a second independent compatibility control.
    if (formFactor) setSegmentValue('psuSegmented', formFactor);

    // Segment synchronization deliberately clears stale exact identity. Restore
    // the selected catalog model after the form factor has been synchronized.
    const psuInput = document.querySelector('#psuCombobox .combobox-input');
    if (psuInput) psuInput.value = selectedLabel;
    const psuClear = document.querySelector('#psuCombobox .combobox-clear-btn');
    if (psuClear) psuClear.style.display = selectedLabel ? 'inline-flex' : 'none';

    setSelectedComponent('psu', {
      name: selectedLabel, brand, model, form_factor: formFactor,
      length_mm: length > 0 ? length : null,
      width_mm: width > 0 ? width : null,
      height_mm: height > 0 ? height : null,
      dimension_known: { length_mm: length > 0, width_mm: width > 0, height_mm: height > 0 },
      dimension_source: item.getAttribute('data-dimension-source') || 'unknown',
      wattage,
      catalogId: item.getAttribute('data-catalog-id') || ''
    });
    if (state.portableBuildHardware) state.portableBuildHardware.psu = null;

    updateSliderBadges();
    debouncedEvaluate();
    maybeLogComponentSelection('psu', {
      name: selectedLabel,
      form_factor: formFactor,
      length_mm: length,
      wattage
    });
  }, null, debouncedEvaluate);

  initSearchableCombobox('gpuCombobox', (item) => {
    invalidateActiveReport();
    const len = parseSpecNum(item.getAttribute('data-gpu-len'), 304);
    const thick = parseSpecNum(item.getAttribute('data-gpu-thick'), 40);
    const height = parseSpecNum(item.getAttribute('data-gpu-height'), 137);
    const slot = parseSpecNum(item.getAttribute('data-gpu-slot'), 2);

    if (dom.gpuL) dom.gpuL.value = len; 
    if (dom.gpuT) dom.gpuT.value = thick; 
    if (dom.gpuH) dom.gpuH.value = height; 

    const selectedLabel = item.getAttribute('data-label') || getComboboxItemLabel(item);
    setSelectedComponent('gpu', { name: selectedLabel, length_mm: len, thickness_mm: thick, height_mm: height, pcie_slots: slot, slot_class: slot, catalogId: item.getAttribute('data-catalog-id') || '' });

    // Exact GPU requirements remain independent of the selected case capability.
    syncPcieSlotsFromThickness(thick, null, { preferredSlots: slot, clampToCase: false });

    updateSliderBadges(); debouncedEvaluate();
    maybeLogComponentSelection('gpu', { name: selectedLabel, length_mm: len, thickness_mm: thick, height_mm: height });
  }, null, debouncedEvaluate);

  initSearchableCombobox('cpuCoolerCombobox', (item) => { 
    invalidateActiveReport();
    const h = parseSpecNum(item.getAttribute('data-val'), 47);
    const len = parseSpecNum(item.getAttribute('data-len'), 95);
    const width = parseSpecNum(item.getAttribute('data-width'), 95);
    if (dom.cpuH) dom.cpuH.value = h;
    const selectedLabel = item.getAttribute('data-label') || getComboboxItemLabel(item);
    setSelectedComponent('cpu', { name: selectedLabel, length_mm: len, width_mm: width, height_mm: h, catalogId: item.getAttribute('data-catalog-id') || '' });
    updateSliderBadges(); debouncedEvaluate(); 
    maybeLogComponentSelection('cpu', { name: selectedLabel, height_mm: h });
  }, null, debouncedEvaluate);

  const initAioSelection = (item) => {
    invalidateActiveReport();
    const size = item.getAttribute('data-rad-size'); 
    if (size) { 
      const btn = document.querySelector(`#radiatorSizeSegmented .segment-btn[data-value="${size}"]`); 
      if (btn) btn.click(); 
    }
    const selectedAioName = item.getAttribute('data-label') || getComboboxItemLabel(item);
    setSelectedComponent('aio', {
      name: selectedAioName,
      model_query: selectedAioName,
      radiator_size_mm: parseInt(size, 10) || 240,
      actual_length_mm: parseSpecNum(item.getAttribute('data-rad-len'), 274),
      actual_width_mm: parseSpecNum(item.getAttribute('data-rad-width'), 120),
      actual_thickness_mm: parseSpecNum(item.getAttribute('data-rad-thick'), 27.2),
      pump_block_height_mm: parseSpecNum(item.getAttribute('data-pump-height'), 45),
      catalogId: item.getAttribute('data-catalog-id') || ''
    });
    debouncedEvaluate();
    maybeLogComponentSelection('aio', { name: selectedAioName, rad_size: parseInt(size, 10) || 240 });
  };

  initSearchableCombobox('aioCoolerCombobox', initAioSelection, null, debouncedEvaluate);
  initSearchableCombobox('aioSearchCombobox', initAioSelection, null, debouncedEvaluate);

  initSearchableCombobox('ramCombobox', (item) => {
    invalidateActiveReport();
    const height = parseSpecNum(item.getAttribute('data-ram-height'), dom.ramH?.value || 35);
    const modules = Math.max(1, Math.min(8, parseInt(item.getAttribute('data-ram-modules'), 10) || 2));
    const selectedLabel = item.getAttribute('data-label') || getComboboxItemLabel(item);
    if (dom.ramH && height > 0) dom.ramH.value = height;
    const moduleBtn = document.querySelector(`#ramModuleSegmented .segment-btn[data-value="${modules}"]`)
      || document.querySelector('#ramModuleSegmented .segment-btn[data-value="2"]');
    if (moduleBtn) moduleBtn.click();
    setSelectedComponent('ram', {
      name: selectedLabel,
      height_mm: height || 35,
      modules,
      memory_type: item.getAttribute('data-memory-type') || '',
      catalogId: item.getAttribute('data-catalog-id') || ''
    });
    state.portableBuildHardware.ram = null;
    updateSliderBadges();
    debouncedEvaluate();
    maybeLogComponentSelection('ram', { name: selectedLabel, height_mm: height, modules });
  }, null, debouncedEvaluate);

  initSearchableCombobox('caseSearchCombobox', (item) => { 
    invalidateActiveReport();
    const rawName = item.getAttribute('data-case-name') || item.textContent.trim();
    if (dom.search) dom.search.value = rawName; 
    state.userExplicitUnselect = false;
    state.portableRouteExitRequested = true;
    state.routeNavigationIntent = 'push';
    evaluateCompatibility(true); 
    maybeLogComponentSelection('case', { name: rawName });
  }, (val) => { 
    invalidateActiveReport();
    if (dom.search) dom.search.value = val;
    // Typing into the case field means the user has left the immutable imported-build route.
    // The first debounced evaluation collapses the tokenized route back to /db/ (or the new
    // exact case route); subsequent keystrokes remain ordinary feed filtering.
    state.portableRouteExitRequested = true;
    debouncedEvaluate(); 
  }, debouncedEvaluate);

  // Initialize Modal, Control Bar, Hardware Insights & Feed Listeners
  initReportModalListeners();
  initHardwareInsightsUi?.();
  initControlBarListeners(dom, evaluateCompatibility);
  initCardFeedDelegation(dom, evaluateCompatibility);
  initMobileDrawers(evaluateCompatibility);

  initPopStateRouter(dom, evaluateCompatibility, applyCaseCompatibleDefaults);
  updateSliderBadges();
  loadApplicationData(dom, evaluateCompatibility, {
    populateHardwareDropdowns, populateCaseSearchDropdown, setupLazyLoadingEngine,
    parseIncomingComparisonRoute, parseIncomingUrlParams, processUrlHashActions,
    updateSliderBadges
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSffApp);
} else {
  initSffApp();
}
